import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import api from './api';

interface User {
  id: string;
  url_id?: string;
  photo_url?: string;
  voice_recording_url?: string;
  tags?: string[];
  location?: {
    lat: number;
    lng: number;
    radius: number;
  };
  launched?: boolean;
  phone_number?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  privateUrl: string | null;
  createAccount: (password: string, dateOfBirth: string, phoneNumber: string, consentTerms: boolean, consentPrivacy: boolean, consentBiometric: boolean, consentPreferences: boolean, kyPublicToken?: string) => Promise<void>;
  login: (urlId: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [privateUrl, setPrivateUrl] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already authenticated
    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('sessionToken');

    if (userId && token) {
      fetchProfile();
    } else {
      setIsLoading(false);
    }
  }, []);

  const createAccount = async (password: string, dateOfBirth: string, phoneNumber: string, consentTerms: boolean, consentPrivacy: boolean, consentBiometric: boolean, consentPreferences: boolean, kyPublicToken?: string) => {
    try {
      const response = await api.post('/api/auth/create-account', {
        password,
        date_of_birth: dateOfBirth,
        phone_number: phoneNumber,
        consent_terms: consentTerms,
        consent_privacy: consentPrivacy,
        consent_biometric: consentBiometric,
        consent_preferences: consentPreferences,
        ky_public_token: kyPublicToken || "",
      });
      const { user_id, url_id, session_token } = response.data;

      // Store credentials
      localStorage.setItem('userId', user_id);
      localStorage.setItem('sessionToken', session_token);

      // Set private URL for display
      setPrivateUrl(`/u/${url_id}`);
      setIsAuthenticated(true);

      // Fetch profile
      await fetchProfile();
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to create account');
    }
  };

  const login = async (urlId: string, password: string) => {
    try {
      const response = await api.post('/api/auth/login', { url_id: urlId, password });
      const { user_id, session_token } = response.data;

      // Store credentials
      localStorage.setItem('userId', user_id);
      localStorage.setItem('sessionToken', session_token);

      setIsAuthenticated(true);

      // Fetch profile
      await fetchProfile();
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear everything
      localStorage.removeItem('userId');
      localStorage.removeItem('sessionToken');
      setIsAuthenticated(false);
      setUser(null);
      setPrivateUrl(null);
      window.location.href = '/';
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get('/api/profile/me');
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      localStorage.removeItem('userId');
      localStorage.removeItem('sessionToken');
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        privateUrl,
        createAccount,
        login,
        logout,
        fetchProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
